import { useMemo } from 'react';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { createArray } from 'utils/helpers/create-fake-array';
import {
  TAdvancedSelectedMarkets,
  TGameMainInfoType,
  TSelectedMarketTypeNames
} from 'interfaces/sportsbook';
import { Market } from 'interfaces/sportsbook-data-levels';
import { gamesGeneralInfoMocked } from './mock';
import { AdvancedMatchCard } from 'newelements/Sportsbook/layouts/advanced/Desktop/__partials/AdvancedMatchCard';
import { AdvancedMatchCardSkeleton } from 'newelements/Sportsbook/layouts/advanced/Desktop/__partials/AdvancedMatchCard/Skeleton';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { GlobalIcon } from 'components/GlobalIcon';
import { NavigationCollapse } from 'components/NavigationCollapse';
import { TStreamData } from 'store/reducers/sport-data';

export enum EGroupGamesBy {
  day,
  league
}

export type TAdvancedMatchCardsViewsProps = {
  gameIds: number[] | null;
  skeletonCount: number;
  isLive: boolean;
  gamesGeneralInfo: Record<string, TGameMainInfoType> | null;
  gamesMarketsInfo?:
    | Record<string, { market: Record<string, Market> | undefined }>
    | undefined;
  selectedMarkets?: TAdvancedSelectedMarkets;
  selectedMarketTypeNames?: TSelectedMarketTypeNames;
  selectedEventIds: string[];
  streamData: Array<[TStreamData]> | null;
  marketsCountsReduxKey?: string;
  groupGamesBy?: EGroupGamesBy;
};

const GAME_RELATED_INFO_SEPARATOR = '$%&';

export const AdvancedMatchCardsDesktop = ({
  gameIds,
  gamesGeneralInfo,
  gamesMarketsInfo,
  selectedMarkets,
  selectedMarketTypeNames,
  skeletonCount,
  isLive,
  selectedEventIds,
  streamData,
  groupGamesBy
}: TAdvancedMatchCardsViewsProps) => {
  const gamesGroupedByKey = useMemo(() => {
    if (gameIds && gamesGeneralInfo && gamesMarketsInfo && selectedMarkets) {
      const result: Record<string, number[]> = {};

      gameIds?.forEach(gameId => {
        if (gamesGeneralInfo[gameId]) {
          const gameGeneralInfo = gamesGeneralInfo[gameId];

          const groupKey =
            groupGamesBy === EGroupGamesBy.league
              ? `${gameGeneralInfo.regionAlias}${GAME_RELATED_INFO_SEPARATOR}${gameGeneralInfo.regionName}${GAME_RELATED_INFO_SEPARATOR}${gameGeneralInfo.competitionName}`
              : '';

          if (groupKey) {
            result[groupKey] = [...(result[groupKey] || []), gameId];
          } else {
            result.all = [...(result.all || []), gameId];
          }
        }
      });

      return result;
    }

    return null;
  }, [gameIds, gamesGeneralInfo, gamesMarketsInfo, selectedMarkets]);

  const createTitleContent = (groupKey: string) => {
    const groupInfoValues = groupKey.split(GAME_RELATED_INFO_SEPARATOR);

    return (
      <span className="groupedByDateHeaderDesktop__leagueInfo">
        <GlobalIcon
          lib="flags"
          size={EIconSize._20}
          name={groupInfoValues[0]}
        />
        {groupInfoValues[1]} : {groupInfoValues[2]}
      </span>
    );
  };

  if (SpringConfigs.MOCKED_DATA) {
    return (
      <AdvancedMatchCard
        key={26422400}
        gameGeneralInfo={gamesGeneralInfoMocked[26422400]}
        gameMarketsInfo={undefined}
        selectedMarkets={{
          first: 'P1XP2',
          second: 'AsianHandicap',
          third: 'OverUnder'
        }}
        selectedEventIds={selectedEventIds}
        isLiveStreamingAvailable={false}
        selectedMarketTypeNames={selectedMarketTypeNames}
      />
    );
  }

  return gamesGroupedByKey &&
    gamesGeneralInfo &&
    gamesMarketsInfo &&
    selectedMarkets
    ? Object.entries(gamesGroupedByKey).map(([groupKey, gameIds]) => (
        <ConditionalWrapper
          key={groupKey}
          condition={!!groupGamesBy}
          wrapper={children => (
            <NavigationCollapse
              key={groupKey}
              defaultActive
              wrapperClassName="groupedByDateWrapperDesktop"
              headerClassName="groupedByDateHeaderDesktop"
              title={createTitleContent(groupKey)}
            >
              {children}
            </NavigationCollapse>
          )}
        >
          <>
            {gameIds.map(
              gameId =>
                !!gamesGeneralInfo[gameId] && (
                  <AdvancedMatchCard
                    key={gameId}
                    gameGeneralInfo={gamesGeneralInfo[gameId]}
                    gameMarketsInfo={gamesMarketsInfo[gameId]?.market}
                    selectedMarkets={selectedMarkets}
                    selectedEventIds={selectedEventIds}
                    isLiveStreamingAvailable={!!streamData?.[gameId]}
                    selectedMarketTypeNames={selectedMarketTypeNames}
                  />
                )
            )}
          </>
        </ConditionalWrapper>
      ))
    : createArray(skeletonCount).map(key => (
        <AdvancedMatchCardSkeleton key={key} isLive={isLive} />
      ));
};
